/* eslint-disable camelcase */
import React, {
  useEffect,
  useCallback,
} from 'react';
import {
  shallowEqual,
  useSelector,
  useDispatch,
} from 'react-redux';
import {
  getStatus,
} from 'redux-resource';
import {
  useHistory,
  useLocation,
} from 'react-router-dom';

import {
  coreActions,
  coreSelectors,
} from '@gymcloud/core';

import {
  decodeParam,
} from 'web-utils';

import {
  uiActions,
} from 'web-actions';

import {
  Icon,
  Spinner,
  NotFound,
  NavigationTabs,
} from 'web-components';

import {
  Flex,
} from 'web-styled';

import Messages from './Messages';
import Footer from './Footer';
import * as S from './styled';

type Props = {
  id: string,
  category: string,
};

const Chat = ({ id, category }: Props) => {
  const history = useHistory();
  const mapState = useCallback(state => ({
    pro: coreSelectors.getClientPro(state),
    profile: coreSelectors.getProfile(state),
    readStatus: getStatus(
      state,
      [
        'messages.requests.fetchMessages.status',
      ],
    ),
    messages: coreSelectors.getMessages(
      id,
      category,
    )(state),
    conversations: coreSelectors.getConversations(state),
  }), [id, category]);
  const {
    pro,
    profile,
    messages,
    readStatus,
    conversations,
  } = useSelector(mapState, shallowEqual);
  const dispatch = useDispatch();
  const location = useLocation();
  const params = decodeParam(location.search);
  const isPro = profile.is_pro;
  const conversation = conversations.find(
    c => c.id === Number(id),
  );

  const tabs = [
    {
      name: 'General',
      to: `/messages/${id}/1${location.search}`,
      unread: conversation?.unreadCategories?.['1'].length > 0,
    },
    {
      name: 'Exercise',
      to: `/messages/${id}/2${location.search}`,
      unread: conversation?.unreadCategories?.['2'].length > 0,
    },
    {
      name: 'Nutrition',
      to: `/messages/${id}/3${location.search}`,
      unread: conversation?.unreadCategories?.['3'].length > 0,
    },
  ];

  useEffect(() => {
    if (id && id !== 'new') {
      dispatch(
        coreActions.requestMessages({
          id,
          qs: {
            page: 1,
            per_page: 50,
            message_category: category,
          },
        }),
      );
    } else if (!isPro && conversations.length) {
      const newUrl = `/messages/${conversations[0].id}/1`;
      history.push(newUrl);
    } else if (!params.recipient) {
      history.push('/messages');
    }
  }, [id, category]);

  const handleSubmit = (values, actions) => {
    dispatch(
      uiActions.requestSendMessage({
        payload: {
          ...values,
          recipient_id: isPro ? (params.recipient || conversation.recipient?.user_id) : pro.id,
          message_category: category,
        },
        conversationId: id,
        callbacks: {
          ...actions,
          onSuccess: (result: any) => {
            actions.onSuccess(result);
            if (result?.conversation_id) {
              history.push(`/messages/${result.conversation_id}/${result.message_category}`);
            }
          },
        },
      }),
    );
  };

  const handleDelete = () => {
    const onConfirmAction = coreActions.requestDeleteEntity({
      id: Number(id),
      resourceType: 'conversations',
    });

    dispatch(
      uiActions.confirmModal({
        modal: {
          title: 'Delete messages',
          bodyText: 'Are you sure you want to delete this conversation?',
          confirmText: 'Delete',
        },
        onSuccess: () => {
          history.push('/messages');
        },
        onConfirmAction,
      }),
    );
  };

  const onMarkRead = () => {
    if (conversation?.has_unread_messages) {
      const ids = messages
        .filter(
          message => !message.is_read,
        )
        .map(message => message.id);
      dispatch(
        coreActions.requestMarkReadMessages({
          ids,
          conversationId: id,
        }),
      );
    }
  };

  return (
    <S.Chat>
      <S.Header>
        {(id === 'new' || !isPro) ? (
          <div />
        ) : (
          <Flex
            width="44px"
            height="44px"
            align="center"
            justify="center"
            clickable
            onClick={handleDelete}
          >
            <Icon
              size="18px"
              type="far fa-trash-alt"
              color="primary"
            />
          </Flex>
        )}
        <NavigationTabs
          tabs={tabs}
        />
      </S.Header>
      {!readStatus.pending && !readStatus.failed && (
        <S.Body>
          <Messages
            profile={profile}
            messages={messages}
            onMarkRead={onMarkRead}
          />
        </S.Body>
      )}

      {readStatus.pending && (
        <Spinner />
      )}

      {readStatus.failed && (
        <NotFound title="Error" />
      )}
      <Footer
        initialValues={{
          body: '',
        }}
        onSubmit={handleSubmit}
      />
    </S.Chat>
  );
};

export default Chat;
